import {
  EyeOutlined,
  RollbackOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Modal,
  Popconfirm,
  Row,
  Skeleton,
  Space,
  Typography,
  message,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import EmailEditor from "../../../shared/emailEditor/EmailEditor";
import EmailPreviewFrame from "../../../shared/emailEditor/EmailPreviewFrame";
import EmailSubjectInput from "../../../shared/emailEditor/EmailSubjectInput";
import useUnsavedChangesGuard from "../../../shared/emailEditor/useUnsavedChangesGuard";
import { clearEmailTemplate } from "../../../store/emailTemplates/emailTemplatesSlice";
import {
  fetchEmailTemplate,
  previewEmailTemplate,
  resetEmailTemplate,
  updateEmailTemplate,
} from "../../../store/emailTemplates/emailTemplatesThunks";

const TEMPLATE_KEY = "renewal";
const SUBJECT_MAX_LENGTH = 255;

const { Text, Title } = Typography;

const EmailTemplatePage = () => {
  const dispatch = useDispatch();
  const { isLoading, isSaving, isPreviewing, data } = useSelector(
    (state) => state.emailTemplates,
  );

  const [subject, setSubject] = useState("");
  const [dirty, setDirty] = useState(false);
  const [failed, setFailed] = useState(false);
  // Bumped whenever the editor must reload its content (initial load, reset)
  const [editorVersion, setEditorVersion] = useState(0);
  const [preview, setPreview] = useState(null);
  const editorRef = useRef(null);
  const draftVersion = useRef(0);

  const markDirty = () => {
    draftVersion.current += 1;
    setDirty(true);
  };

  const showTemplate = (template) => {
    setSubject(template.subject);
    setEditorVersion((version) => version + 1);
    setDirty(false);
  };

  useEffect(() => {
    dispatch(fetchEmailTemplate({ key: TEMPLATE_KEY }))
      .unwrap()
      .then(showTemplate)
      .catch(() => setFailed(true));
    return () => {
      dispatch(clearEmailTemplate());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useUnsavedChangesGuard(dirty);

  const fields = data?.fields ?? [];
  const subjectFields = fields.filter((field) => field.key !== "signature");

  const currentDraft = () => ({
    key: TEMPLATE_KEY,
    subject: subject.trim(),
    body: editorRef.current?.getHTML() ?? "",
  });

  const validateDraft = () => {
    if (!subject.trim()) {
      message.error("Please enter a subject line.");
      return false;
    }
    if (subject.trim().length > SUBJECT_MAX_LENGTH) {
      message.error("The subject line is too long. Please shorten it.");
      return false;
    }
    if (editorRef.current?.isEmpty()) {
      message.error("The email body can't be empty.");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (isSaving || !validateDraft()) return;
    const version = draftVersion.current;
    try {
      const saved = await dispatch(updateEmailTemplate(currentDraft())).unwrap();
      if (draftVersion.current === version) {
        showTemplate(saved);
        message.success("Template saved. New renewal emails will use this wording.");
      } else {
        message.info("Earlier edits were saved. Your newer edits are still unsaved.");
      }
    } catch (_) {
      // Validation problems are shown by the API error notifications
    }
  };

  const handlePreview = async () => {
    if (!validateDraft()) return;
    try {
      const result = await dispatch(previewEmailTemplate(currentDraft())).unwrap();
      setPreview(result);
    } catch (_) {
      // Shown by the API error notifications
    }
  };

  const handleReset = async () => {
    if (isSaving) return;
    const version = draftVersion.current;
    try {
      const restored = await dispatch(
        resetEmailTemplate({ key: TEMPLATE_KEY }),
      ).unwrap();
      if (draftVersion.current === version) {
        showTemplate(restored);
        message.success("The default wording has been restored.");
      } else {
        message.info("The default was restored. Your newer edits are still unsaved.");
      }
    } catch (_) {
      // Shown by the API error notifications
    }
  };

  const lastUpdated = data?.last_updated_by
    ? `Last updated by ${data.last_updated_by} on ${data.last_updated_at}`
    : "Nobody has changed the default wording yet.";

  if (failed) {
    return (
      <Alert
        type="error"
        showIcon
        message="Error: Something went wrong while loading the email template."
        className="default-margin"
      />
    );
  }

  return (
    <Card className="body-card-content">
      <Skeleton active loading={isLoading || !data}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col>
            <Title level={5} style={{ margin: 0 }}>
              Renewal email
            </Title>
            <Text type="secondary">{lastUpdated}</Text>
          </Col>
          <Col>
            <Space wrap>
              <Popconfirm
                title="Restore the default wording?"
                description="Your edits and the saved subject and body will be replaced by the original template."
                okText="Restore"
                onConfirm={handleReset}
                disabled={isSaving}
              >
                <Button
                  size="small"
                  icon={<RollbackOutlined />}
                  disabled={isSaving || (data?.customized === false && !dirty)}
                >
                  Reset to default
                </Button>
              </Popconfirm>
              <Button
                size="small"
                icon={<EyeOutlined />}
                loading={isPreviewing}
                onClick={handlePreview}
              >
                Preview
              </Button>
              <Button
                size="small"
                type="primary"
                icon={<SaveOutlined />}
                loading={isSaving}
                disabled={!dirty}
                onClick={handleSave}
              >
                Save
              </Button>
            </Space>
          </Col>
        </Row>

        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16, marginBottom: 16 }}
          message="This template is used for every renewal email. Fields such as Customer name or Application link are filled in for each customer when the email is sent, and the Signature field becomes the sender's own signature."
        />

        <div style={{ marginBottom: 16 }}>
          <Text strong>Subject</Text>
          <div style={{ marginTop: 4 }}>
            {data && (
              <EmailSubjectInput
                key={editorVersion}
                defaultValue={subject}
                fields={subjectFields}
                onChange={(value) => {
                  setSubject(value);
                  markDirty();
                }}
              />
            )}
          </div>
        </div>

        <Text strong>Body</Text>
        <div style={{ marginTop: 4 }}>
          {data && (
            <EmailEditor
              key={editorVersion}
              ref={editorRef}
              defaultValue={data.body}
              fields={fields}
              signature={{ chip: true }}
              onChange={markDirty}
              placeholder="Write the renewal email…"
              minHeight={420}
            />
          )}
        </div>

        <Modal
          open={preview !== null}
          onCancel={() => setPreview(null)}
          title="Preview with sample data"
          width={760}
          footer={[
            <Button key="close" onClick={() => setPreview(null)}>
              Close
            </Button>,
          ]}
        >
          <div style={{ marginBottom: 12 }}>
            <strong>Subject:</strong> {preview?.subject}
          </div>
          {preview && <EmailPreviewFrame html={preview.html} />}
        </Modal>
      </Skeleton>
    </Card>
  );
};

export default EmailTemplatePage;
